import classNames from "classnames";
import { Link } from "gatsby";
import pcComachi from "images/introduction/PC.png";
import credit from "images/introduction/credit.png";
import apps from "images/introduction/apps.png";
import React from "react";
import Style from "styles/components/introduction.module.scss";
import DesignStyle from "styles/components/design.module.scss";
import CommonStyle from "styles/components/common.module.scss";
import useScrollAnimation from "hooks/useScrollAnimation";

// 業種別サンプル画像
import talent from 'images/industry/talent.png';
import agriculture from 'images/industry/agriculture.png';
import cosmetics from 'images/industry/cosmetics.png';
import kids from 'images/industry/kids.png';
import sampleOlivePc from 'images/industry/sample_OLIVE_PC.png';
import japanese from 'images/industry/japanese.png';
import craftsman from 'images/industry/craftsman.png';
import d2c from 'images/industry/d2c.png';
import sports from 'images/industry/sports.png';
import linen from 'images/industry/linen.png';
import atelierPc from 'images/industry/atelier_pc.png';
import atelierSp from 'images/industry/atelier_sp.png';
import wellnessPc from 'images/industry/wellness_pc.png';
import wellnessSp from 'images/industry/wellness_sp.png';
import sampleCozyPc from 'images/industry/sample_COZY_PC.png';
import sampleOceanPc from 'images/industry/sample_OCEAN_PC.png';
import sampleStridePc from 'images/industry/sample_STRIDE_PC.png';
import sampleGreenPc from 'images/industry/sample_GREEN_PC.png';

// 業種別サンプルデータ
const industries = [
  { img: talent, label: 'タレント・アーティスト' },
  { img: agriculture, label: '農業・水産業' },
  { img: cosmetics, label: 'コスメ・美容サロン' },
  { img: kids, label: 'ペット・キッズ・教育' },
  { img: sampleOlivePc, label: '宿泊・アウトドア' },
  { img: japanese, label: '和食・酒販・伝統工芸' },
  { img: craftsman, label: '生産者直販・職人工房' },
  { img: d2c, label: 'D2Cブランド' },
  { img: sports, label: 'スポーツ' },
  { img: linen, label: 'ベビー・ハンドメイド' },
  { img: atelierPc, label: 'ハンドメイド・アクセサリー' },
  { img: atelierSp, label: 'ハンドメイド・アクセサリー' },
  { img: wellnessPc, label: '医療・介護・福祉用品' },
  { img: wellnessSp, label: '医療・介護・福祉用品' },
  { img: sampleCozyPc, label: 'ペット・キッズ・教育' },
  { img: sampleOceanPc, label: '生産者直販・職人工房' },
  { img: sampleStridePc, label: 'スポーツ' },
  { img: sampleGreenPc, label: 'D2Cブランド' },
];

// 業種別サンプル（2行レイアウト + 横スクロール）
const IndustrySamples = () => {
  const [sectionRef, isVisible] = useScrollAnimation(0.1);

  const layoutPattern = [
    { type: 'tall', items: [0] },           // タレント・アーティスト
    { type: 'stack', items: [14, 15] },     // ペット・キッズ + 生産者直販
    { type: 'tall', items: [13] },          // 医療・介護・福祉用品
    { type: 'stack', items: [16, 17] },     // スポーツ + D2Cブランド
    { type: 'tall', items: [9] },           // ベビー・ハンドメイド
    { type: 'stack', items: [4, 5] },       // 宿泊・アウトドア + 和食・酒販
    { type: 'tall', items: [11] },          // ハンドメイド・アクセサリー
    { type: 'stack', items: [1, 2] },       // 農業・水産業 + コスメ・美容
    { type: 'tall', items: [8] },           // スポーツ
    { type: 'tall', items: [6] },           // 生産者直販・職人工房
    { type: 'stack', items: [10, 12] },     // ハンドメイド + 医療・介護
    { type: 'tall', items: [7] },           // D2Cブランド
    { type: 'tall', items: [3] },           // ペット・キッズ・教育
  ];

  const doubledPattern = [...layoutPattern, ...layoutPattern];

  return (
    <div
      ref={sectionRef}
      className={classNames(DesignStyle.industrySection, { [DesignStyle.industrySection__visible]: isVisible })}
    >
      <div className={DesignStyle.industryHeader}>
        <p className={DesignStyle.industryEyebrow}>CASE STUDY</p>
        <h2 className={DesignStyle.industryTitle}>
          さまざまな業種でご利用いただけます。
        </h2>
      </div>
      <div className={DesignStyle.industrySliderWrapper}>
        <div className={DesignStyle.industrySlider}>
          {doubledPattern.map((block, blockIndex) => (
            <div
              key={`block-${blockIndex}`}
              className={classNames(
                DesignStyle.industryBlock,
                DesignStyle[`industryBlock__${block.type}`]
              )}
              style={{ animationDelay: `${blockIndex * 0.2}s` }}
            >
              {block.items.map((itemIndex) => {
                const industry = industries[itemIndex % industries.length];
                return (
                  <div className={DesignStyle.industryCard} key={`card-${blockIndex}-${itemIndex}`}>
                    <img
                      src={industry.img}
                      alt={industry.label}
                      className={DesignStyle.industryImg}
                    />
                    <div className={DesignStyle.industryOverlay}>
                      <p className={DesignStyle.industryLabel}>{industry.label}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// 売れるまで、お金はかからない（文言のみ）
const PriceCatch = () => {
  const [sectionRef, isVisible] = useScrollAnimation(0.05);

  return (
    <div
      ref={sectionRef}
      className={classNames(Style.introduction, { [Style.introduction__visible]: isVisible })}
    >
      <span className={Style.introduction__eyebrow}>Pricing</span>
      <div className={Style.mask}>
        <div className={classNames(Style.introduction__catch, Style.revealText)}>
          <span className={Style.introduction__comachicart__hasBlueText}>
            売れるまで、お金はかからない。
          </span>
        </div>
      </div>
      <div className={classNames(Style.introduction__pricing)}>
        <div className={Style.mask}>
          <span className={classNames(Style.revealText, Style.revealText__delay1)}>初期費用　<strong>0円</strong></span>
        </div>
        <div className={Style.mask}>
          <span className={classNames(Style.revealText, Style.revealText__delay2)}>月額費用　<strong>0円～</strong></span>
        </div>
        <div className={Style.mask}>
          <span className={classNames(Style.revealText, Style.revealText__delay3)}>決済手数料　<strong>3.2%</strong></span>
        </div>
      </div>
      <div className={Style.mask}>
        <div className={classNames(Style.introduction__comachicart, Style.revealText, Style.revealText__delay4)}>
          ショップを育てる時間を、コストが邪魔しない。<br />
          それが、
          <span className={Style.introduction__comachicart__hasBlueText}>
            comachicart
          </span>
          の設計です
        </div>
      </div>
    </div>
  );
};

// コマチカートが選ばれる3つの理由
const Features = () => {
  const [sectionRef, isVisible] = useScrollAnimation(0.15);

  return (
    <section ref={sectionRef} className={classNames(Style.features, { [Style.features__visible]: isVisible })}>
      <div className={Style.features__head}>
        <span className={Style.features__eyebrow}>Features</span>
        <h2 className={Style.features__title}>
          コマチカートが選ばれる<strong>3つの理由</strong>
        </h2>
      </div>

      <div className={Style.features__cards}>
        {/* CARD 01 */}
        <div className={classNames(Style.card, { [Style.card__visible]: isVisible })}>
          <div className={Style.card__inner}>
            <div className={Style.card__head}>
              <span className={Style.card__num}>01</span>
              <h3 className={Style.card__title}>月額無料でスタート</h3>
            </div>
            <div className={Style.card__img}>
              <img src={pcComachi} alt="コマチカート 管理画面" />
            </div>
            <div className={Style.card__body}>
              <ul className={Style.card__list}>
                <li>初期費用・月額費用が0円</li>
                <li>無料でネットショップ開設</li>
              </ul>
            </div>
          </div>
        </div>

        {/* CARD 02 */}
        <div className={classNames(Style.card, { [Style.card__visible]: isVisible })}>
          <div className={Style.card__inner}>
            <div className={Style.card__head}>
              <span className={Style.card__num}>02</span>
              <h3 className={Style.card__title}>クレジット決済費用 3.2%</h3>
            </div>
            <div className={Style.card__img}>
              <img src={credit} alt="クレジット決済" />
            </div>
            <div className={Style.card__body}>
              <p className={Style.card__note}>※VISA/MASTERは3.2%、AMEX/JCB/DINERSは3.3%</p>
              <ul className={Style.card__list}>
                <li>Comachiは決済手数料3.2%</li>
              </ul>
            </div>
          </div>
        </div>

        {/* CARD 03 */}
        <div className={classNames(Style.card, { [Style.card__visible]: isVisible })}>
          <div className={Style.card__inner}>
            <div className={Style.card__head}>
              <span className={Style.card__num}>03</span>
              <h3 className={Style.card__title}>基本機能が充実、売上UP！</h3>
            </div>
            <div className={Style.card__img}>
              <img src={apps} alt="アプリ機能" />
            </div>
            <div className={Style.card__body}>
              <ul className={Style.card__list}>
                <li>運営に必要な販促機能は標準搭載</li>
                <li>アプリ機能で運営の幅を広げる</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className={CommonStyle.moreBtnWrapper}>
        <Link className={CommonStyle.moreBtn} to="/func/">
          詳しくはこちら
          <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path d="M5 12h14M13 6l6 6-6 6" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </Link>
      </div>
    </section>
  );
};

// 後方互換のため維持
const Introduction = () => {
  return (
    <>
      <PriceCatch />
      <IndustrySamples />
      <Features />
    </>
  );
};

export default Introduction;
export { PriceCatch, Features, IndustrySamples };
